<template>
  <v-data-table
      class="firmware-diagnostics-table"
      :headers="headers"
      :items="items"
      :loading="loading"
      :items-per-page="10"
      :footer-props="{'items-per-page-options': [10, 20, 50]}"
      :hide-default-footer="items.length <= 10"
      :no-data-text="$t('firmware.diagnostic.none')"
      dense
  >
    <!-- Quand, et par qui : une seule colonne, deux lignes -->
    <template #item.createdAt="{item}">
      <div class="text-no-wrap font-weight-medium">{{ formatDate(item.createdAt, 'DD/MM/YY HH:mm') }}</div>
      <div class="text-caption text--secondary text-no-wrap">{{ item.requestedByName }}</div>
    </template>
    <template #item.chargingPointName="{item}">
      <router-link :to="{name: 'charging-points.detail.manage', params: {id: item.chargingPointId}}">{{ item.chargingPointName || item.chargingPointId }}</router-link>
    </template>

    <!-- L'état, et ce qu'il veut dire en clair dessous -->
    <template #item.status="{item}">
      <div class="d-flex align-center" style="gap: 6px">
        <v-icon small :color="statusColor(item.status)">{{ statusIcon(item.status) }}</v-icon>
        <span class="font-weight-medium" :class="`${statusColor(item.status)}--text`">{{ $t('firmware.diagnostic.statuses.' + item.status) }}</span>
        <v-chip v-if="item.protocol" x-small outlined class="protocol" :title="item.location">{{ item.protocol.toUpperCase() }}</v-chip>
      </div>
      <v-progress-linear v-if="item.open" indeterminate height="2" class="my-1" :color="statusColor(item.status)" />
      <div v-if="item.failureCode" class="text-caption error--text">
        {{ $t('firmware.diagnostic.failures.' + item.failureCode) }}
        <span v-if="item.failureDetail" class="failure-detail">{{ item.failureDetail }}</span>
      </div>
      <div v-else-if="item.open" class="text-caption text--secondary">{{ $t('firmware.diagnostic.waiting.' + item.status) }}</div>
    </template>

    <!-- Le fichier sur une ligne, sa taille et sa conservation dessous -->
    <template #item.file="{item}">
      <template v-if="item.fileName">
        <div class="file-name" :title="item.fileName">{{ item.fileName }}</div>
        <div class="text-caption text--secondary text-no-wrap">
          {{ size(item.size) }}<template v-if="item.deleteAt"> · {{ $t('firmware.diagnostic.kept-until', {date: formatDate(item.deleteAt, 'DD/MM/YYYY')}) }}</template>
        </div>
      </template>
      <div v-else-if="item.announcedFileName" class="file-name text--secondary" :title="item.announcedFileName">{{ item.announcedFileName }}</div>
      <span v-else class="text--disabled">—</span>
    </template>

    <!-- Télécharger, et dessous qui l'a déjà fait (données personnelles : chaque téléchargement est tracé) -->
    <template #item.actions="{item}">
      <template v-if="item.status === 'received'">
        <v-btn small outlined color="primary" class="compact" :loading="downloading === item.id" @click="download(item)">
          <v-icon small left>mdi-download</v-icon>{{ $t('firmware.diagnostic.download') }}
        </v-btn>
        <v-tooltip v-if="item.downloads.length" bottom>
          <template #activator="{on}">
            <div class="text-caption text--secondary mt-1 downloads" v-on="on">{{ $tc('firmware.diagnostic.downloaded-times', item.downloads.length, {n: item.downloads.length}) }}</div>
          </template>
          <div class="text-caption mb-1">{{ $t('firmware.diagnostic.downloaded-by') }}</div>
          <div v-for="(download, index) in item.downloads" :key="index">{{ formatDate(download.at) }} — {{ download.by }}</div>
        </v-tooltip>
      </template>
    </template>
  </v-data-table>
</template>

<script>
import FirmwareRepository from "@repository/FirmwareRepository";

const COLORS = {requested: 'blue-grey', accepted: 'primary', uploading: 'primary', received: 'success', failed: 'error', expired: 'grey', deleted: 'grey'}
const ICONS = {requested: 'mdi-send-clock-outline', accepted: 'mdi-check', uploading: 'mdi-cloud-upload-outline', received: 'mdi-file-check-outline', failed: 'mdi-alert-circle-outline', expired: 'mdi-timer-sand-complete', deleted: 'mdi-delete-outline'}

/**
 * Des demandes de diagnostic, une ligne chacune : quand et par qui, l'état en clair (et le refus de la borne tel qu'elle
 * l'a dit), le fichier, le téléchargement — tracé, qui l'a fait au survol.
 */
export default {
  props: {
    items: {type: Array, required: true},
    loading: {type: Boolean, default: false},
    // Dans la rubrique Firmware (tout le parc) : la borne de chaque demande.
    showChargingPoint: {type: Boolean, default: false},
  },

  data() {
    return {downloading: null}
  },

  computed: {
    headers() {
      return [
        {text: this.$t('firmware.diagnostic.requested-at'), value: 'createdAt', width: 150},
        this.showChargingPoint ? {text: this.$t('firmware.charging-point'), value: 'chargingPointName'} : null,
        {text: this.$t('firmware.diagnostic.status'), value: 'status', sortable: false, width: 280},
        {text: this.$t('firmware.diagnostic.file'), value: 'file', sortable: false},
        {text: '', value: 'actions', sortable: false, width: 150},
      ].filter(Boolean)
    },
  },

  methods: {
    download(item) {
      this.downloading = item.id
      FirmwareRepository.downloadDiagnostic(item).then(() => {
        this.$emit('downloaded')
      }).catch(err => {
        this.notifyError(err)
      }).finally(() => {
        this.downloading = null
      })
    },

    size(bytes) {
      if (bytes === null || bytes === undefined) {
        return '-'
      }
      return bytes < 1024 * 1024 ? `${Math.max(1, Math.round(bytes / 1024))} Ko` : `${(bytes / 1024 / 1024).toFixed(1)} Mo`
    },

    statusColor(status) {
      return COLORS[status] || 'grey'
    },

    statusIcon(status) {
      return ICONS[status] || 'mdi-help-circle-outline'
    },
  },
}
</script>

<style scoped>
/* Tout à gauche, d'un bout à l'autre : le style global centre les colonnes, ici il mélangeait centré et aligné. */
.firmware-diagnostics-table >>> td,
.firmware-diagnostics-table >>> th {
  text-align: left !important;
}
.firmware-diagnostics-table >>> td {
  padding-top: 10px !important;
  padding-bottom: 10px !important;
  vertical-align: top;
}
/* Le style global écarte l'icône de 20 px : trop pour un petit bouton. */
.compact >>> .v-icon--left {
  margin-right: 6px !important;
}
.downloads {
  cursor: help;
  white-space: nowrap;
}
.file-name {
  max-width: 320px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.protocol {
  font-size: 10px !important;
  height: 16px !important;
}
.failure-detail {
  display: block;
  font-family: monospace;
  word-break: break-word;
}
</style>
